import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, AfterContentInit } from '@angular/core';
import { Borrower } from 'app/models/dto/borrower.model';
import { BorrowerService } from 'app/services/borrower/borrower.service';
import { take } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { Select, Store } from '@ngxs/store';
import { SubscribeToCallNotificationChannels, SubscribeToNotificationChannels, UnsubscribeFromNotificationChannels } from 'app/store/notification/notification.actions';
import { LenderUser } from 'app/models/dto/lender-user.model';
import { PusherService } from 'app/modules/shared/services/pusher.service';
import { environment } from 'environments/environment';
import { GoOnline } from 'app/store/lender-user/lender-user/lender-user.actions';
import { GetClaimRestrictions, SubscribeToJumpballNotify, SubscribeToUnattemptedLeads, UnsubscribeFromJumpballNotify, UnsubscribeFromUnattemptedLeads } from 'app/store/claim-lead/claim-lead.actions';
import { Router } from '@angular/router';
import { isMobileDevice } from 'app/util/mobile/isMobile';
import { ActivityState } from 'app/store/activity/activity.state';
import { LatestActivitiesViewed } from 'app/store/activity/activity.actions';

@Component({
  selector: 'app-global-header',
  templateUrl: './global-header.component.html',
  styleUrls: ['./global-header.component.scss']
})
export class GlobalHeaderComponent implements OnInit, OnDestroy, AfterContentInit {

  @Select(ActivityState.latestUnreadActivitiesCount) unreadNotifications$: Observable<number>;
  @Output() globalHeaderLoaded = new EventEmitter();
  @Output() notificationDrawerToggle = new EventEmitter<boolean>();
  @Output() cxDrawerToggle = new EventEmitter<boolean>();
  @Input() user: LenderUser;
  lenderUser: any;
  canSubmitCxInteractions: boolean;
  isMobile: boolean = false;
  canViewDealsTab: boolean = false;
  canViewCxPerfTab: boolean = false;

  constructor(
    private borrowerService: BorrowerService,
    private store: Store,
    private pusherService: PusherService,
    private router: Router
  ) {}

  ngOnInit() {
    this.subscribeToNotificationChannels();

    // Only funding managers need to subscribe to these channels
    if (this.user?.roles?.includes('FM Workflow')) {
      this.subscribeToJumpballNotify();
      this.subscribeToUnattemptedLeads();
    }

    this.canSubmitCxInteractions = this.user?.team?.type?.includes('customer_service') || this.user?.team?.name?.includes('Test');

    this.handleMobileMenu();
  }

  ngAfterContentInit(): void {
    this.globalHeaderLoaded.emit();
  }

  handleCxDrawer() {
    this.cxDrawerToggle.emit(true);
  }

  handleNotificationDrawer() {
    this.notificationDrawerToggle.emit(true);
    this.store.dispatch(new LatestActivitiesViewed());
  }

  subscribeToUnattemptedLeads() {
    this.store.dispatch(new GetClaimRestrictions());

    if (
      !this.user?.permissions?.includes('claimRestriction.unattempted.warn')
      && !this.user?.permissions?.includes('claimRestriction.unattempted.restrict')
    ) {
      return;
    }
    this.store.dispatch(new SubscribeToUnattemptedLeads(this.user));
  }

  subscribeToNotificationChannels() {
    this.store.dispatch(new SubscribeToNotificationChannels(this.user));
    this.store.dispatch(new SubscribeToCallNotificationChannels(this.user));
  }

  subscribeToJumpballNotify() {
    this.store.dispatch(new SubscribeToJumpballNotify(this.user));
  }

  viewBorrower(borrower: Borrower) {
    if (!borrower.deleted) {
      this.router.navigate(['/borrower/', borrower.id]);
      return;
    }

    this.borrowerService.update(borrower.id, {
      deleted: false,
    }).pipe(take(1)).subscribe(() => {
      this.router.navigate(['/borrower/', borrower.id]);
    });
  }

  goOnline() {
    this.store.dispatch(new GoOnline(this.user.id));
  }

  handleMobileMenu() {
    this.isMobile = isMobileDevice();
    if (this.isMobile) {
      this.canViewDealsTab = this.user?.permissions?.includes('viewTeamDeals') || this.user?.permissions?.includes('viewFMTeamDeals');
      this.canViewCxPerfTab = this.user?.permissions?.includes('viewCXPerformanceTab');
    }
  }

  handleMobileRoute(value: string) {
    switch (value) {
      case 'tasks':
        this.router.navigate(['/']);
        return;
      case 'businesses':
        this.router.navigate(['/list']);
        return;
      case 'funnel':
        this.router.navigate(['/funnel']);
        return;
      case 'deals':
        this.router.navigate(['/deals']);
        return;
      case 'performance':
        this.router.navigate(['/performance']);
        return;
      case 'cxPerformance':
        this.router.navigate(['/cx-performance']);
        return;
      default:
        return;
    }
  }

  ngOnDestroy(): void {
    this.pusherService.unsubscribeRaw(`LS-global-${environment.environment}-channel`);
    this.store.dispatch(new UnsubscribeFromNotificationChannels());
    this.store.dispatch(new UnsubscribeFromUnattemptedLeads(this.user.id));
    this.store.dispatch(new UnsubscribeFromJumpballNotify(this.user));
  }
}
